import React, {useState} from "react";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import ElementsPickDialog from "../../elements/ElementsPickDialog";
import {ArchimateRelationship} from "../../../../../common/archimate/ArchimateRelationship";
import {SaveButton} from "../../../../../components/button/SaveButton";
import {CancelButton} from "../../../../../components/button/CancelButton";
import {ElementDto} from "../../../../../common/apis/element/ElementDto";
import {_transl} from "../../../../../store/localization/TranslMessasge";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import AlertDialog, {AlertDialogType} from "../../../../../components/dialogs/AlertDialog";
import {TextFieldWithMoreIcon} from "../../../../../components/TextFieldWithAdornmentIcon";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import Grid from "../../../../../components/dialogs/Grid";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import elementsService from "../../elements/service/ElementService";
import {formatElement} from "../../elements/ElementFormatter";
import {RelationshipsTranslationKey} from "../RelationshipsTranslationKey";

enum Direction {
    OUTGOING = "outgoing",
    INCOMING = "incoming"
}

interface RelationshipsCreateDialogProps {
    title: string,
    onClosed: () => void,
    selectedRows: Array<ElementDto>,
    refetchFilter: () => void,
}

export function RelationshipBulkCreateDialog(props: RelationshipsCreateDialogProps) {
    const {title, onClosed, selectedRows, refetchFilter} = props;

    const [isUnsuccessfullyValidated, setUnsuccessfullyValidated] = useState<boolean>(false);
    const [errorDialogOpened, setErrorDialogOpened]  = useState(false);
    const [pickDialogOpened, setPickDialogOpened] = useState(false);
    const [pickedElement, setPickedElement] = useState<ElementDto | undefined>(undefined);
    const [direction, setDirection] = useState<string | null>(null);
    const [directionError, setDirectionError] = useState<string | undefined>(undefined);
    const [type, setType] = useState<ArchimateRelationship | null>(null);
    const [typeError, setTypeError] = useState<string | undefined>(undefined);
    const [pickedElementError, setPickedElementError] = useState<string | undefined>(undefined);
    const formattedCommonElementName = formatElement(pickedElement);

    function onElementsPicked(elements: ElementDto[]) {
        if (elements.length === 1) {
            const element = elements[0];
            if (!canCreateRelationships(element, selectedRows)) {
                showErrorDialog();
            } else {
                isUnsuccessfullyValidated && validatePickedElement(element);
                setPickedElement(element)
                closePickDialog();
            }
        }
    }

    function canCreateRelationships(commonElement: ElementDto, otherElements: ElementDto[]): boolean {
        return commonElement.acl.canUpdate || otherElements.some(element => element.acl.canUpdate);
    }

    function showPickDialog() {
        setPickDialogOpened(true)
    }

    function closePickDialog() {
        setPickDialogOpened(false);
    }

    function showErrorDialog() {
        setErrorDialogOpened(true);
    }

    function closeErrorDialog() {
        setErrorDialogOpened(false);
    }

    const validateDirection = (direction: string | null): boolean => {
        if (direction === null) {
            setDirectionError(_transl(CommonTranslationKey.FILL_OUT_ITEM_FROM_LIST));
            return false;
        } else {
            setDirectionError(undefined);
            return true;
        }
    };

    const validateType = (type: ArchimateRelationship | null): boolean => {
        if (type === null) {
            setTypeError(_transl(CommonTranslationKey.FILL_OUT_ITEM_FROM_LIST));
            return false;
        } else {
            setTypeError(undefined);
            return true;
        }
    };

    const validatePickedElement = (pickedElement: ElementDto | undefined): boolean => {
        if (pickedElement === undefined) {
            setPickedElementError(_transl(CommonTranslationKey.FILL_OUT_ITEM_FROM_LIST));
            return false;
        } else {
            setPickedElementError(undefined);
            return true;
        }

    };

    const isFormValid = (): boolean => {
        const isDirectionValid = validateDirection(direction);
        const isTypeValid = validateType(type);
        const isPickedElementValid = validatePickedElement(pickedElement);

        return isDirectionValid && isTypeValid && isPickedElementValid;
    };

    function saveChanges() {
        if (isFormValid()) {
            const identifier = pickedElement!.identifier;
            const relationshipDirection = direction === Direction.OUTGOING;

            if (identifier !== undefined) {
                createRelationship(identifier, relationshipDirection);
            }
        } else {
            setUnsuccessfullyValidated(true)
        }
    }

    function createRelationship(identifier: string, relationshipDirection: boolean) {
        elementsService.createRelationship(identifier, selectedRows.map(el => el.identifier), type!.standardNames[0], relationshipDirection)
            .then(() => {
                refetchFilter();
                onClosed();
                Snackbar.success(_transl(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
            })
            .catch(() => {
                Snackbar.error(_transl(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED));
            });
    }

    function getLabelForDirection(direction: Direction) {
        return (direction === Direction.OUTGOING)
                ? _transl(RelationshipsTranslationKey.COMMON_ELEMENT_IS_SOURCE)
                : _transl(RelationshipsTranslationKey.COMMON_ELEMENT_IS_TARGET);
    }

    return (
        <>
            <Dialog open maxWidth={"md"} onClose={onClosed}>

                <DialogTitle id="scroll-dialog-title"
                             title={title}
                             onDialogClosed={onClosed}/>

                <DialogContent>
                    <Grid container>
                        <Grid item xs={12}>
                            <SingleSelectField
                                    id={"direction-field"}
                                    label={_transl(RelationshipsTranslationKey.RELATIONSHIP_DIRECTION)}
                                    value={direction}
                                    onChange={(value) => {
                                        isUnsuccessfullyValidated && validateDirection(value);
                                        setDirection(value);
                                    }}
                                    options={[Direction.OUTGOING, Direction.INCOMING]}
                                    getOptionLabel={(value) => getLabelForDirection(value as Direction)}
                                    required={true}
                                    errorMessage={directionError}/>
                        </Grid>
                        <Grid item xs={12}>
                            <SingleSelectField
                                    id={"type-field"}
                                    label={_transl(RelationshipsTranslationKey.RELATIONSHIP_TYPE)}
                                    value={type}
                                    options={ArchimateRelationship.values()}
                                    getOptionLabel={(value) => value?.visibleName || ""}
                                    onChange={(value) => {
                                        isUnsuccessfullyValidated && validateType(value);
                                        setType(value);
                                    }}
                                    required={true}
                                    errorMessage={typeError}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldWithMoreIcon key={formattedCommonElementName}
                                                   id="assignedItem-field"
                                                   required={true}
                                                   value={formattedCommonElementName}
                                                   label={_transl(RelationshipsTranslationKey.COMMON_ELEMENT)}
                                                   errorMessage={pickedElementError}
                                                   tooltipTitle={_transl(RelationshipsTranslationKey.FIND_COMMON_ELEMENT)}
                                                   onClick={() => showPickDialog()} />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <SaveButton onClick={saveChanges}/>
                    <CancelButton onClick={onClosed}/>
                </DialogActions>
            </Dialog>

            <ElementsPickDialog isOpened={pickDialogOpened}
                                isMultiSelection={false}
                                onElementsPicked={onElementsPicked}
                                onDialogClosed={closePickDialog} />

            <AlertDialog open={errorDialogOpened}
                         title={_transl(RelationshipsTranslationKey.INSUFFICIENT_PERMISSIONS)}
                         text={_transl(RelationshipsTranslationKey.INSUFFICIENT_PERMISSIONS_TO_CREATE_RELATIONSHIP_BETWEEN_ELEMENTS)}
                         type={AlertDialogType.WARNING}
                         onClose={() => closeErrorDialog()} />
        </>
    );
}
